import { BUCKET_LABELS } from "../../api";
import { Button, cx } from "../../components";
import { LEDGER_SCENARIO, useLedgerFlow } from "../../motion";
import type { LedgerBucket } from "../../motion";
import styles from "./Vitrine.module.css";

/* Géométrie du dessin, en unités de la viewBox 640 × 360. Les quatre colonnes
   sont à 140 unités les unes des autres ; les seaux montent de 200 unités au
   plus, depuis leur base à 290. */
const COLONNES: { bucket: LedgerBucket; x: number; classe: string }[] = [
  { bucket: "available", x: 100, classe: styles.seauAvailable },
  { bucket: "reserved", x: 240, classe: styles.seauReserved },
  { bucket: "consumed", x: 380, classe: styles.seauConsumed },
  { bucket: "expired", x: 520, classe: styles.seauExpired },
];
const BASE = 290;
const HAUTEUR = 200;
const LARGEUR = 88;
const HAUTEUR_PILULE = 175;

const LEGENDES: Record<(typeof LEDGER_SCENARIO)[number]["step"], string> = {
  achat: "Achat d'un pack : 1 000 tokens entrent, au taux du jour, figé.",
  reserve: "Un projet ouvert réserve 400 tokens. Le disponible baisse d'autant.",
  consomme: "250 tokens imputés sur des feuilles de temps validées.",
  libere: "Projet clos : le reliquat de 150 revient au disponible.",
  expire: "50 tokens périmés. La somme des quatre seaux n'a pas bougé.",
};

const formatEntier = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });

function colonne(bucket: LedgerBucket | null): number {
  if (bucket === null) {
    return COLONNES[0].x;
  }
  return COLONNES.find((c) => c.bucket === bucket)?.x ?? COLONNES[0].x;
}

/**
 * Le grand livre en cinq gestes : quatre seaux qui se remplissent et se
 * vident l'un dans l'autre, et une somme qui ne bouge pas. Le dessin est
 * décoratif pour l'assistance technique ; le texte masqué à côté dit la même
 * chose, et la légende écrite sous le dessin nomme chaque seau.
 */
export function VitrineLedger() {
  const { ref, replay } = useLedgerFlow();

  return (
    <div className={styles.livre}>
      <svg
        ref={ref}
        className={styles.livreDessin}
        viewBox="0 0 640 360"
        aria-hidden="true"
        focusable="false"
      >
        {LEDGER_SCENARIO.map((s) => (
          <text
            key={s.step}
            data-anim="ledger-caption"
            data-step={s.step}
            x="320"
            y="26"
            textAnchor="middle"
            className={styles.livreLegende}
          >
            {LEGENDES[s.step]}
          </text>
        ))}

        {COLONNES.map((c) => (
          <g key={c.bucket}>
            <rect
              x={c.x - LARGEUR / 2}
              y={BASE - HAUTEUR}
              width={LARGEUR}
              height={HAUTEUR}
              rx="10"
              className={styles.livrePiste}
            />
            <rect
              data-anim="ledger-fill"
              data-bucket={c.bucket}
              x={c.x - LARGEUR / 2}
              y={BASE - HAUTEUR}
              width={LARGEUR}
              height={HAUTEUR}
              rx="10"
              className={cx(styles.livreSeau, c.classe)}
            />
            <text
              data-anim="ledger-count"
              data-bucket={c.bucket}
              x={c.x}
              y={BASE - HAUTEUR - 12}
              textAnchor="middle"
              className={styles.livreCompteur}
            />
            <text x={c.x} y={BASE + 22} textAnchor="middle" className={styles.livreNom}>
              {BUCKET_LABELS[c.bucket]}
            </text>
          </g>
        ))}

        <rect x="56" y="326" width="508" height="6" rx="3" className={styles.livrePiste} />
        <rect data-anim="ledger-total" x="56" y="326" width="508" height="6" rx="3" className={styles.livreTotal} />
        <text data-anim="ledger-equation" x="320" y="354" textAnchor="middle" className={styles.livreEquation} />

        {LEDGER_SCENARIO.map((s) => (
          <g
            key={s.step}
            data-anim="ledger-flow"
            data-step={s.step}
            data-x0={colonne(s.from)}
            data-y0={s.from === null ? -30 : HAUTEUR_PILULE}
            data-x1={colonne(s.to)}
            data-y1={HAUTEUR_PILULE}
            className={styles.livrePilule}
          >
            <rect x="-34" y="-15" width="68" height="30" rx="15" />
            <text x="0" y="5" textAnchor="middle">
              {(s.from === null ? "+" : "") + formatEntier.format(s.amount)}
            </text>
          </g>
        ))}
      </svg>

      <p className="lde-visually-hidden">
        Illustration animée du grand livre : un achat fait entrer mille tokens dans le disponible ; un
        projet en réserve quatre cents ; deux cent cinquante sont consommés sur feuilles de temps ; à la
        clôture, cent cinquante reviennent au disponible ; cinquante expirent. À chaque étape, disponible
        plus réservé plus consommé plus expiré font toujours mille.
      </p>

      <div className={styles.livrePied}>
        <ul className={styles.livreSeaux} aria-label="Les quatre seaux">
          {COLONNES.map((c) => (
            <li key={c.bucket}>
              <span className={cx(styles.livrePastille, c.classe)} aria-hidden="true" />
              {BUCKET_LABELS[c.bucket]}
            </li>
          ))}
        </ul>
        <Button variant="ghost" size="sm" onClick={replay}>
          Rejouer
        </Button>
      </div>
    </div>
  );
}
